/**
 * Локальный контроллер боя на настоящем engine (I1): забежка без сервера. Держит текущий бой, ведёт переходы между
 * волнами (предложение усилений, выбор, итог, откат), считает монеты и отдаёт журнал волны наружу.
 *
 * Что заменит I2 (серверная версия):
 * - `onWaveLog(log)` — сюда приходит `WaveLog` каждой завершённой волны: его надо отправить серверу;
 * - `nextSeed(wave)` — локальный ГПСЧ; сервер выдаёт сид в `PlayerState.next` (контроллер берёт его, если `next.wave`
 *   совпадает с волной забежки);
 * - покупки (`upgrade`) — сейчас `applyPurchase` в памяти; с сервером — запрос API и новый `PlayerState` в стор.
 *
 * Контроллер не знает ни DOM, ни стора: всё приходит через {@link GameDeps}, поэтому тестируется без браузера.
 * Он же — {@link SnapshotSource} цикла кадров: `step()` делает тик боя, `current()` — последний снимок.
 */
import type {
  AttackDefense,
  BalanceData,
  BattleSnapshot,
  PlayerCommand,
  PlayerState,
  PurchaseRequest,
  RunState,
  TimedCommand,
  WaveLog,
  WaveResult,
} from "@newfungame/contracts";
import { retryLockAfterLoss, WAVE_LOG_FORMAT } from "@newfungame/contracts";
import {
  applyPurchase,
  boostOffer,
  createBattle,
  isBossWave,
  refreshRun,
  restartRun,
  settleWave,
  type Battle,
  type BoostOption,
} from "@newfungame/engine";
import type { SnapshotSource } from "../app/loop";
import type { WindowRoute } from "../app/store";

export interface GameDeps {
  balance: BalanceData;
  /** Подтверждённое состояние игрока; `null` — ещё не вошли (бой идёт на начальной забежке). */
  getPlayer(): PlayerState | null;
  setPlayer(player: PlayerState): void;
  /** Итог волны прибавил монеты к игроку раньше, чем сервер принял журнал (только с сервером). */
  onCoinsCredited?(coins: number): void;
  setBoostOffer(options: BoostOption[] | null): void;
  openWindow(w: WindowRoute): void;
  /** Волна пройдена, бой стоит до выбора усиления. Окна отсюда встают в очередь перед выбором. */
  onWaveCleared?(wave: number, boss: boolean): void;
  /** Журнал завершённой волны — для I2 (отправка на сервер). */
  onWaveLog?(log: WaveLog): void;
  /** Сид следующей волны, если сервер его не дал. По умолчанию — случайный. */
  nextSeed?(wave: number): number;
  clientVersion?: string;
  /**
   * Сервер выдаёт сид: после волны следующая не стартует, пока синхронизация не ответит ({@link GameController.synced})
   * или не сдастся ({@link GameController.syncOffline}). Без флага сид локальный (тесты, `?fake=1`).
   */
  awaitServerSeed?: boolean;
  /** Началась волна (I2: от этого момента считается «не раньше длины волны»). */
  onWaveStart?(wave: number): void;
  /** Начальное состояние, пока игрок не вошёл. */
  fallbackRun?(): RunState;
  /** Игрок включил автонавыки: у VIP они включаются сами на старте каждой волны. */
  autoPref?(): boolean;
  /** Время сервера: от него считается таймер после проигрыша, пока не ответит сервер. */
  serverNow?(): number;
}

/**
 * `fight` — идёт бой; `boost` — ждём выбор усиления; `next` — ждём «Дальше» (после проигрыша);
 * `sync` — волна сдана, ждём сид следующей от сервера.
 */
export type GamePhase = "fight" | "boost" | "next" | "sync" | "attack";

/** Бой атаки участка (бета, «Карта»): то, что выдал сервер при старте атаки. */
export interface AttackSetup {
  seed: number;
  defense: AttackDefense;
  start: RunState;
}

/** Итог боя атаки: журнал команд и результат движка — из них клиент собирает `AttackLog`. */
export interface AttackOutcome {
  commands: TimedCommand[];
  result: WaveResult;
}

export interface GameController extends SnapshotSource {
  command(cmd: PlayerCommand): void;
  chooseBoost(index: number): void;
  nextWave(): void;
  upgrade(purchases: PurchaseRequest[]): void;
  /** Начать забежку заново из `player.run` (сервер закрыл забежку, игрок вошёл). */
  restart(): void;
  /** Сервер ответил по журналу: взять его сид; если подтверждённая забежка ушла от локальной — начать с неё. */
  synced(): void;
  /** Сети нет: ждать сид нечего, играем дальше на локальном (журнал уйдёт из очереди, ответ откатит). */
  syncOffline(): void;
  /** Игрок стал VIP: автонавыки открываются сразу, посреди волны. */
  vipGranted(): void;
  phase(): GamePhase;
  /**
   * Бой атаки участка поверх волны: волна ставится «на паузу» (её бой, фаза и снимок сохраняются) и возвращается
   * в `endAttack`. Слушатель вызывается один раз, когда бой атаки закончился.
   */
  startAttack(setup: AttackSetup, onOver: () => void): void;
  /** Итог боя атаки; `null`, пока бой идёт или атаки нет. */
  attackOutcome(): AttackOutcome | null;
  /** Закончить атаку и вернуть волну. */
  endAttack(): void;
  /** Текущая волна для живого эфира (MADR 0040): забежка на старте, сид, команды и тик; `null`, пока сид ждёт сервера. */
  live(): LiveWave | null;
}

/** Волна как её видит эфир — всё, что нужно зрителю, чтобы собрать тот же бой. */
export interface LiveWave {
  run: RunState;
  seed: number;
  vip: boolean;
  commands: TimedCommand[];
  tick: number;
}

const randomSeed = (): number => Math.floor(Math.random() * 0x1_0000_0000) >>> 0;

function initialRun(balance: BalanceData): RunState {
  const run: RunState = {
    runId: "00000000-0000-4000-8000-000000000001",
    wave: 1,
    heroes: [{ heroId: "gunner", slot: 0, level: 1, stars: 0 }],
    tech: {},
    boosts: [],
    hqHp: 0,
    autoSkills: false,
  };
  return restartRun(balance, run);
}

export function createGameController(deps: GameDeps): GameController {
  const { balance } = deps;
  const nextSeed = deps.nextSeed ?? randomSeed;
  const clientVersion = deps.clientVersion ?? "dev";

  let run: RunState;
  let seed: number;
  let battle: Battle;
  let snap: BattleSnapshot;
  let phase: GamePhase = "fight";
  let offer: BoostOption[] = [];
  let pendingNext: { run: RunState; seed: number } | null = null;
  /** Сид следующей волны ещё должен прийти от сервера. */
  let seedPending = false;
  /** Игрок уже нажал «Дальше» / выбрал усиление, ждём только сид. */
  let wantStart = false;
  /** Итог текущей волны уже засчитан в монеты игрока: `earnedThisWave` снимка не прибавлять второй раз. */
  let settled = false;

  /** Состав забежки из текущего прогресса игрока (покупки между волнами). */
  const refreshed = (r: RunState): RunState => {
    const p = deps.getPlayer();
    return p ? refreshRun(balance, r, p.heroes, p.tech) : r;
  };

  const applyAutoPref = (): void => {
    if (battle.autoSkillsAllowed() && deps.autoPref?.()) battle.command({ type: "setAutoSkills", enabled: true });
  };

  const begin = (r: RunState, s: number): void => {
    run = r;
    seed = s;
    const p = deps.getPlayer();
    // покупки между волнами меняют состав: подтверждённая забежка игрока — та, что идёт в бой
    if (p && JSON.stringify(p.run) !== JSON.stringify(run)) deps.setPlayer({ ...p, run });
    battle = createBattle({
      balance,
      run,
      seed,
      walletCoins: p?.coins,
      autoSkillsAllowed: p ? p.vip.active || p.player.adminTools : false,
    });
    applyAutoPref();
    settled = false;
    deps.onWaveStart?.(run.wave);
    snap = battle.snapshot();
    phase = "fight";
    offer = [];
    pendingNext = null;
  };

  const startFromPlayer = (): void => {
    const p = deps.getPlayer();
    if (!p) {
      begin((deps.fallbackRun ?? (() => initialRun(balance)))(), nextSeed(1));
      return;
    }
    begin(refreshed(p.run), p.next.wave === p.run.wave ? p.next.seed : nextSeed(p.run.wave));
  };

  /** Итог волны → игрок: монеты, забежка, лучшая волна; журнал — наружу. */
  const settle = (result: WaveResult): boolean => {
    const commands = battle.commands();
    const settlement = settleWave(balance, { run, seed, result, commands }, { bestWave: deps.getPlayer()?.bestWave });
    if (!settlement.ok) return false;
    deps.onWaveLog?.({
      format: WAVE_LOG_FORMAT,
      runId: run.runId,
      wave: run.wave,
      seed,
      balanceVersion: balance.version,
      clientVersion,
      start: run,
      ...(battle.autoSkillsAllowed() ? { vip: true } : {}),
      commands,
      result,
    });
    const p = deps.getPlayer();
    seedPending = !!deps.awaitServerSeed && p !== null;
    const seedNext = seedPending ? 0 : nextSeed(settlement.run.wave);
    if (p) {
      if (seedPending) deps.onCoinsCredited?.(settlement.coins);
      deps.setPlayer({
        ...p,
        coins: p.coins + settlement.coins,
        run: settlement.run,
        // с сервером сид даёт только он: до ответа `next` остаётся прежним
        next: seedPending ? p.next : { wave: settlement.run.wave, seed: seedNext },
        bestWave: settlement.outcome === "cleared" ? Math.max(p.bestWave, run.wave) : p.bestWave,
        // ответ сервера заменит: окно проигрыша показывает таймер сразу
        retryLock:
          settlement.outcome === "lost"
            ? retryLockAfterLoss(run.wave, p.vip.active, deps.serverNow?.() ?? Date.now())
            : p.retryLock,
        // предложение приходит только в ответе сервера: прошлое к этому итогу не относится
        revive: null,
      });
    }
    pendingNext = { run: settlement.run, seed: seedNext };
    settled = true;
    return true;
  };

  const finish = (): void => {
    const result = battle.result();
    if (!result) return;
    if (result.outcome === "cleared") {
      offer = boostOffer(balance, run, seed);
      phase = "boost";
      const boss = isBossWave(balance, run.wave);
      if (boss) deps.openWindow({ kind: "bossDefeated", wave: run.wave, coins: result.coins });
      deps.onWaveCleared?.(run.wave, boss);
      if (offer.length > 0) deps.setBoostOffer(offer);
      else chooseBoostAt(null);
      return;
    }
    if (!settle(result)) return;
    phase = "next";
    deps.openWindow({
      kind: "defeat",
      wave: run.wave,
      rollbackTo: pendingNext!.run.wave,
    });
  };

  /** Выбор усиления (или пропуск, когда выбирать не из чего) → итог волны → следующая волна. */
  const chooseBoostAt = (index: number | null): void => {
    const result = battle.result();
    if (phase !== "boost" || !result) return;
    if (index !== null) {
      const option = offer[index];
      if (!option) return;
      battle.command({ type: "chooseBoost", index, boostId: option.boostId });
    }
    if (!settle(result)) {
      // недопустимый выбор: предложение остаётся, окно откроется снова
      deps.setBoostOffer(offer);
      return;
    }
    startNext();
  };

  /** Начать следующую волну; если сид ещё у сервера — ждать ответ (`sync`). */
  const startNext = (): void => {
    const next = pendingNext!;
    if (seedPending) {
      wantStart = true;
      phase = "sync";
      return;
    }
    wantStart = false;
    begin(refreshed(next.run), next.seed);
  };

  /** Снимок для HUD: после засчитывания волны её монеты уже в игроке. */
  const view = (s: BattleSnapshot): BattleSnapshot =>
    settled && s.wallet.earnedThisWave > 0 ? { ...s, wallet: { ...s.wallet, earnedThisWave: 0 } } : s;

  startFromPlayer();

  /** Волна, отложенная на время боя атаки. */
  let parked: { battle: Battle; snap: BattleSnapshot; phase: GamePhase } | null = null;
  let attackOver: (() => void) | null = null;

  return {
    current: () => view(snap),
    step() {
      if (phase === "sync") return view(snap);
      battle.step();
      snap = battle.snapshot();
      if (phase === "attack") {
        if (attackOver && battle.isOver()) {
          const done = attackOver;
          attackOver = null;
          done();
        }
        return snap;
      }
      if (phase === "fight" && battle.isOver()) finish();
      return view(snap);
    },
    command: (cmd) => {
      if (phase === "fight" || phase === "attack") battle.command(cmd);
    },
    chooseBoost: (index) => chooseBoostAt(index),
    nextWave() {
      if (phase !== "next" || !pendingNext) return;
      startNext();
    },
    upgrade(purchases) {
      const current = deps.getPlayer();
      if (!current) return;
      let p: PlayerState = current;
      for (const req of purchases) {
        const r = applyPurchase(balance, p, req);
        if (r.ok) p = r.state;
      }
      deps.setPlayer(p);
    },
    restart() {
      // новая забежка отменяет и отложенную волну, и идущую атаку
      parked = null;
      attackOver = null;
      seedPending = false;
      wantStart = false;
      startFromPlayer();
    },
    synced() {
      seedPending = false;
      const p = deps.getPlayer();
      const next = pendingNext;
      if (!next) return;
      // подтверждённая забежка совпала с локальной — берём серверный сид; иначе (откат, исправление) — с неё
      const same = p && p.run.runId === next.run.runId && p.run.wave === next.run.wave;
      if (p && same && p.next.wave === next.run.wave) {
        pendingNext = { run: next.run, seed: p.next.seed };
        if (wantStart) startNext();
      } else if (p && !same) {
        startFromPlayer();
      } else {
        pendingNext = { run: next.run, seed: nextSeed(next.run.wave) };
        if (wantStart) startNext();
      }
    },
    syncOffline() {
      if (!seedPending) return;
      seedPending = false;
      if (pendingNext) pendingNext = { run: pendingNext.run, seed: nextSeed(pendingNext.run.wave) };
      if (wantStart) startNext();
    },
    vipGranted() {
      if (phase !== "fight" || battle.autoSkillsAllowed()) return;
      battle.allowAutoSkills();
      applyAutoPref();
    },
    phase: () => phase,
    startAttack(setup, onOver) {
      if (phase === "attack") return;
      parked = { battle, snap, phase };
      // автонавыки — как решает движок по волне забежки: сервер повторяет бой с теми же настройками
      battle = createBattle({ balance, run: setup.start, seed: setup.seed, attack: setup.defense });
      snap = battle.snapshot();
      phase = "attack";
      attackOver = onOver;
    },
    attackOutcome() {
      const result = phase === "attack" ? battle.result() : null;
      return result ? { commands: battle.commands(), result } : null;
    },
    endAttack() {
      if (phase !== "attack" || !parked) return;
      ({ battle, snap, phase } = parked);
      parked = null;
      attackOver = null;
    },
    live: () =>
      phase === "sync" || phase === "attack"
        ? null
        : { run, seed, vip: battle.autoSkillsAllowed(), commands: battle.commands(), tick: battle.tick() },
  };
}
